import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import { gql } from '../../../graphql/client';
import { Q_FETCH_ALL_APP_DATA, Q_LIST_CORE_COMPONENTS } from '../../../graphql/operations';
import { useEditorStore } from '../store/editorStore';
import { ComponentPalette } from './ComponentPalette';
import { ScreensPanel } from './ScreensPanel';
import { Splitter } from './Splitter';
import { VerticalSplit } from './VerticalSplit';
import { ViewTree } from './ViewTree';
import { Canvas } from './Canvas';
import { PropertyPanel } from './PropertyPanel';
import { PackagingControls } from '../../packaging/PackagingControls';
import { AIChatPanel } from '../../ai/AIChatPanel';

/* ---------------------------------------------------------------------------
 * Resizable column widths.
 *
 * Persisted in localStorage so the layout sticks across reloads. Defaults
 * mirror the previous hard-coded `aside` widths so first-load looks
 * identical. The splitters between columns clamp to LEFT_MIN..LEFT_MAX
 * / RIGHT_MIN..RIGHT_MAX to keep the canvas usable.
 * ------------------------------------------------------------------------- */
const LEFT_DEFAULT = 280;
const RIGHT_DEFAULT = 320;
const LEFT_MIN = 220;
const LEFT_MAX = 480;
const RIGHT_MIN = 260;
const RIGHT_MAX = 560;
const STORAGE_KEY_LEFT = 'forge.builder.leftWidth';
const STORAGE_KEY_RIGHT = 'forge.builder.rightWidth';

function loadWidth(key: string, fallback: number, min: number, max: number): number {
  if (typeof window === 'undefined') return fallback;
  const raw = window.localStorage.getItem(key);
  if (!raw) return fallback;
  const n = Number(raw);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(min, Math.min(max, n));
}

export function BuilderScreen() {
  const { appId } = useParams<{ appId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const setAppId = useEditorStore((s) => s.setAppId);
  const selectView = useEditorStore((s) => s.selectView);

  const [leftWidth, setLeftWidth] = useState(() =>
    loadWidth(STORAGE_KEY_LEFT, LEFT_DEFAULT, LEFT_MIN, LEFT_MAX),
  );
  const [rightWidth, setRightWidth] = useState(() =>
    loadWidth(STORAGE_KEY_RIGHT, RIGHT_DEFAULT, RIGHT_MIN, RIGHT_MAX),
  );

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY_LEFT, String(leftWidth));
  }, [leftWidth]);
  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY_RIGHT, String(rightWidth));
  }, [rightWidth]);

  useEffect(() => {
    setAppId(appId ?? null);
  }, [appId, setAppId]);

  const data = useQuery({
    queryKey: ['app-data', appId],
    enabled: !!appId,
    queryFn: () => gql(Q_FETCH_ALL_APP_DATA, { appId }),
  });

  const palette = useQuery({
    queryKey: ['core-components'],
    queryFn: () => gql(Q_LIST_CORE_COMPONENTS),
  });

  /*
   * Deep-link handoff from App Settings → Builder. App Settings links
   * navigate here with `?selectView=<viewId>`; once the app data has
   * loaded, we promote that id into the editor store and strip the
   * param from the URL so back/forward / future copies don't keep
   * re-applying it. We only consume the param once per mount via the
   * ref guard below — selecting a different view in the builder
   * shouldn't re-trigger this effect.
   */
  const consumedSelectParam = useRef(false);
  useEffect(() => {
    if (consumedSelectParam.current) return;
    if (!data.data) return;
    const targetId = searchParams.get('selectView');
    if (!targetId) return;

    const views = (data.data as { View?: { id: string }[] }).View ?? [];
    const exists = views.some((v) => v.id === targetId);
    if (exists) selectView(targetId);

    consumedSelectParam.current = true;
    const next = new URLSearchParams(searchParams);
    next.delete('selectView');
    setSearchParams(next, { replace: true });
  }, [data.data, searchParams, setSearchParams, selectView]);

  if (!appId) return <div style={styles.empty}>Missing appId</div>;
  if (data.isLoading || palette.isLoading) return <div style={styles.empty}>Loading…</div>;
  if (data.error)
    return <div style={styles.empty}>App data error: {(data.error as Error).message}</div>;
  if (palette.error)
    return <div style={styles.empty}>Palette error: {(palette.error as Error).message}</div>;

  return (
    <div style={styles.shell}>
      <header style={styles.header}>
        <Link to={`/apps/${appId}`} style={styles.back}>← App Settings</Link>
        <span style={styles.title}>View Builder</span>
        <PackagingControls appId={appId} />
      </header>
      <div style={styles.workspace}>
        <aside style={{ ...styles.left, width: leftWidth }}>
          <VerticalSplit
            storageKey="forge.builder.leftHeights"
            sections={[
              {
                key: 'palette',
                defaultHeight: 220,
                minHeight: 120,
                children: <ComponentPalette palette={palette.data as any} />,
              },
              {
                key: 'screens',
                defaultHeight: 200,
                minHeight: 100,
                children: <ScreensPanel appData={data.data as any} />,
              },
              {
                key: 'tree',
                defaultHeight: 0, // last section is flex-grown
                minHeight: 120,
                children: <ViewTree appData={data.data as any} />,
              },
            ]}
          />
        </aside>
        <Splitter
          width={leftWidth}
          onResize={setLeftWidth}
          side="left"
          min={LEFT_MIN}
          max={LEFT_MAX}
        />
        <main style={styles.center}>
          <Canvas appData={data.data as any} />
        </main>
        <Splitter
          width={rightWidth}
          onResize={setRightWidth}
          side="right"
          min={RIGHT_MIN}
          max={RIGHT_MAX}
        />
        <aside style={{ ...styles.right, width: rightWidth }}>
          {/*
            Right rail: PropertyPanel for view-level edits (top), AI
            Chat panel for natural-language edits (bottom). Both share
            the right-column real estate via VerticalSplit so authors
            can resize the proportion that matches their working style.
          */}
          <VerticalSplit
            storageKey="forge.builder.rightHeights"
            sections={[
              {
                // PropertyPanel on top — controlled height, drag the
                // splitter to adjust. Default ~half the column.
                key: 'props',
                defaultHeight: 420,
                minHeight: 220,
                children: (
                  <PropertyPanel appData={data.data as any} palette={palette.data as any} />
                ),
              },
              {
                // AIChatPanel last — takes remaining height (VerticalSplit
                // flex-grows the last section). Honours minHeight so the
                // composer is always reachable.
                key: 'ai-chat',
                defaultHeight: 0,
                minHeight: 240,
                children: <AIChatPanel appId={appId} />,
              },
            ]}
          />
        </aside>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  shell: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    fontFamily: 'system-ui, sans-serif',
    backgroundColor: '#F1F5F9',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    padding: '8px 16px',
    borderBottom: '1px solid #CBD5E1',
    backgroundColor: '#fff',
    boxShadow: '0 1px 0 rgba(15, 23, 42, 0.04)',
  },
  back: { fontSize: 13, color: '#64748B', textDecoration: 'none' },
  title: { fontSize: 14, fontWeight: 600, color: '#0F172A', flex: 1 },
  workspace: { flex: 1, display: 'flex', overflow: 'hidden' },
  left: {
    backgroundColor: '#fff',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    boxShadow: 'inset -1px 0 0 #E2E8F0',
    minHeight: 0,
  },
  center: {
    flex: 1,
    padding: 16,
    overflow: 'auto',
    backgroundColor: '#F1F5F9',
  },
  right: {
    backgroundColor: '#fff',
    overflow: 'auto',
    flexShrink: 0,
    boxShadow: 'inset 1px 0 0 #E2E8F0',
    minHeight: 0,
  },
  empty: { padding: 48, textAlign: 'center', color: '#64748B' },
};
